import React, { useCallback } from 'react';

import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';

import StyledSelect from '~/shared/components/StyledSelect';
import { mobile } from '~/techstyle-shared/react-components';
import { useIntl } from '~/techstyle-shared/react-intl';

const SelectWrapper = styled.span`
  flex: 1;

  ${StyledSelect.Fieldset} {
      width: 100%;

      ${({ error }) =>
        error &&
        css`
          & > label {
            color: ${({ theme }) => theme.colors.error};
          }
        `}

      select {
        border-width: 1px;
        border-color: ${({ theme }) => theme.colors.quizDropdownBorder};
        border-radius: ${({ theme }) => theme.borderRadiusInput};

        height: ${({ showZip }) => (showZip ? '48px' : '72px')};

        ${mobile`
          font-size: 16px;
        `}

        ${({ error }) =>
          error &&
          css`
            border-color: ${({ theme }) => theme.colors.error};
          `}
      }

      > span {
        display: none;
      }

      label {
        ${({ theme }) => theme.quiz.selectFieldSetLabel};
      }

      #hdyh-quiz-dropdown-native-select {
        border: unset;
        border-radius: ${({ theme }) => theme.borderRadiusInput};

        ~ label{
          ${mobile`
            ${({ $selectedValue }) =>
              $selectedValue &&
              css`
                top: 0px;
                left: 0px;
              `}
          `}
        }
      }
    }
  }

  ${StyledSelect.Label} {
    font-size: 16px;
    padding-left: 8px;
    top: 8px;

    ${({ selectedValue }) =>
      selectedValue &&
      `
        top: 0;
        font-size: 12px;
        padding: 7px 0 0 8px;
      `};
  }
`;

const DEFAULT_VALUE = { value: '', label: '' };

const QuizNativeSelect = ({
  handleFormUpdate,
  id,
  name,
  className,
  setFieldValue,
  setErrors,
  showZip,
  values,
  errors,
  options,
  label,
  additionalStyles,
  hasError,
  ...rest
}) => {
  const intl = useIntl();
  const handleChange = useCallback(
    (e) => {
      const { [name]: fieldError, ...restErrors } = errors;
      setErrors({ ...restErrors });
      const found = [...options, DEFAULT_VALUE].find(
        (o) => o.value === e.target.value
      );
      setFieldValue(name, found);
      handleFormUpdate({
        ...values,
        [name]: found,
      });
    },
    [setErrors, errors, options, setFieldValue, name, handleFormUpdate, values]
  );

  const { value: selectedValue } = values[name] || {};

  const error =
    hasError && errors?.[name]?.id ? intl.formatMessage(errors[name]) : false;

  return (
    <SelectWrapper
      showZip={showZip}
      selectedValue={selectedValue}
      error={error}
      style={additionalStyles}
      $selectedValue={!!selectedValue}
      id="quiz-native-select"
    >
      <StyledSelect
        id={id}
        name={name}
        className={className}
        onChange={handleChange}
        value={selectedValue}
        error={error}
        label={label}
        {...rest}
      >
        <option {...DEFAULT_VALUE} />
        {options.map(({ value, label }) => (
          <option value={value} key={label}>
            {label}
          </option>
        ))}
      </StyledSelect>
    </SelectWrapper>
  );
};

QuizNativeSelect.propTypes = {
  additionalStyles: PropTypes.any,
  className: PropTypes.string,
  errors: PropTypes.object.isRequired,
  handleFormUpdate: PropTypes.func.isRequired,
  hasError: PropTypes.bool.isRequired,
  id: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(PropTypes.object).isRequired,
  setErrors: PropTypes.func.isRequired,
  setFieldValue: PropTypes.func.isRequired,
  showZip: PropTypes.bool.isRequired,
  values: PropTypes.object.isRequired,
};

export default QuizNativeSelect;
